/**
 * Vault lifecycle: create, probe, unlock, list, delete.
 *
 * A vault is one encrypted record per account. The plaintext inside it is a small JSON
 * document holding the private scalar (and the mnemonic, when there is one) — it exists
 * only as a `Uint8Array` between `crypto.subtle.decrypt` and `JSON.parse`, and is wiped
 * immediately after. It is never written to `localStorage`, `sessionStorage`, or IndexedDB
 * in the clear.
 */

import { fromBase64, toBase64, toHex, utf8ToBytes, wipe } from './bytes';
import {
  CorruptVaultError,
  InvalidInputError,
  VaultAlreadyExistsError,
  VaultNotFoundError,
  WeakPasswordError,
} from './errors';
import { partyIdFor } from './identity';
import { identityFromKeyMaterial, keyMaterialFromSecret, type KeyMaterial } from './keys';
import { createSession, type Session } from './session';
import {
  assertStoredVault,
  getVaultStorage,
  VAULT_VERSION,
  type StoredVault,
} from './storage';
import {
  decryptPayload,
  encryptPayload,
  KDF_NAME,
  PBKDF2_ITERATIONS,
  type EncryptedPayload,
} from './vault-crypto';

/** Shortest password accepted by {@link createVault}. */
export const MIN_PASSWORD_LENGTH = 8;

/** Options for {@link createVault}. */
export interface CreateVaultOptions {
  /** Replace an existing vault for this account. Off by default: overwriting loses keys. */
  overwrite?: boolean;
}

/** The decrypted document. `v` guards against a future format change. */
interface VaultPayload {
  v: number;
  privateKeyHex: string;
  mnemonic?: string;
}

function requireAccountId(accountId: string): string {
  if (typeof accountId !== 'string' || accountId.trim().length === 0) {
    throw new InvalidInputError('accountId is required');
  }
  return accountId;
}

function requirePassword(password: string): string {
  if (typeof password !== 'string' || password.length < MIN_PASSWORD_LENGTH) {
    throw new WeakPasswordError(MIN_PASSWORD_LENGTH);
  }
  return password;
}

function toEncryptedPayload(record: StoredVault): EncryptedPayload {
  return {
    salt: fromBase64(record.salt),
    iv: fromBase64(record.iv),
    ciphertext: fromBase64(record.ciphertext),
    iterations: record.kdf.iterations,
  };
}

/**
 * Encrypts `secret` under `password` and persists it for `accountId`.
 *
 * @param secret a BIP-39 recovery phrase (from `generateWallet`) or a 64-hex private key
 *               (an existing custodial recovery key).
 * @throws {VaultAlreadyExistsError} unless `options.overwrite` is set.
 * @throws {WeakPasswordError} for passwords shorter than {@link MIN_PASSWORD_LENGTH}.
 */
export async function createVault(
  accountId: string,
  secret: string,
  password: string,
  options?: CreateVaultOptions,
): Promise<void> {
  requireAccountId(accountId);
  requirePassword(password);

  const storage = getVaultStorage();
  if (!options || !options.overwrite) {
    const existing = await storage.get(accountId);
    if (existing) throw new VaultAlreadyExistsError(accountId);
  }

  const material: KeyMaterial = keyMaterialFromSecret(secret);
  let plaintext: Uint8Array | null = null;
  try {
    const identity = identityFromKeyMaterial(material);
    const payload: VaultPayload = { v: VAULT_VERSION, privateKeyHex: toHex(material.privateKey) };
    if (material.mnemonic) payload.mnemonic = material.mnemonic;

    plaintext = utf8ToBytes(JSON.stringify(payload));
    const encrypted = await encryptPayload(plaintext, password, accountId, VAULT_VERSION);

    const now = Date.now();
    const previous = await storage.get(accountId).catch(() => null);
    const record: StoredVault = {
      version: VAULT_VERSION,
      accountId,
      publicKeyHex: identity.publicKeyHex,
      fingerprint: identity.fingerprint,
      salt: toBase64(encrypted.salt),
      iv: toBase64(encrypted.iv),
      ciphertext: toBase64(encrypted.ciphertext),
      kdf: { name: KDF_NAME, iterations: encrypted.iterations || PBKDF2_ITERATIONS },
      createdAt: previous ? previous.createdAt : now,
      updatedAt: now,
    };
    await storage.put(record);
  } finally {
    wipe(plaintext);
    wipe(material.privateKey);
  }
}

/** True when this device holds a vault for that account. */
export async function hasVault(accountId: string): Promise<boolean> {
  requireAccountId(accountId);
  const record = await getVaultStorage().get(accountId);
  return record !== null;
}

/** Account ids with a vault on this device. */
export async function listVaults(): Promise<string[]> {
  return getVaultStorage().listIds();
}

/** Removes a vault. Irreversible without the recovery phrase; succeeds if already absent. */
export async function deleteVault(accountId: string): Promise<void> {
  requireAccountId(accountId);
  await getVaultStorage().remove(accountId);
}

/** The encrypted record, for UIs that want to show an identity while still locked. */
export async function getVaultRecord(accountId: string): Promise<StoredVault | null> {
  requireAccountId(accountId);
  return getVaultStorage().get(accountId);
}

/**
 * Decrypts the vault and returns a live {@link Session}.
 *
 * @throws {VaultNotFoundError} when no vault exists for the account.
 * @throws {WrongPasswordError} when AES-GCM authentication fails — which is the *only*
 *         signal available, since nothing here talks to a server.
 * @throws {CorruptVaultError} when the plaintext is unusable or the record's advertised
 *         public key does not match the key inside it.
 */
export async function unlockVault(accountId: string, password: string): Promise<Session> {
  requireAccountId(accountId);
  if (typeof password !== 'string' || password.length === 0) {
    throw new InvalidInputError('password is required');
  }

  const raw = await getVaultStorage().get(accountId);
  if (!raw) throw new VaultNotFoundError(accountId);
  const record = assertStoredVault(raw);

  const plaintext = await decryptPayload(toEncryptedPayload(record), password, accountId, record.version);

  let payload: VaultPayload;
  try {
    payload = JSON.parse(decodeUtf8(plaintext)) as VaultPayload;
  } catch (err) {
    throw new CorruptVaultError('Vault decrypted but its contents are not readable');
  } finally {
    wipe(plaintext);
  }

  if (!payload || typeof payload.privateKeyHex !== 'string') {
    throw new CorruptVaultError('Vault payload has no private key');
  }

  const material = keyMaterialFromSecret(payload.privateKeyHex);
  if (payload.mnemonic) material.mnemonic = payload.mnemonic;

  const identity = identityFromKeyMaterial(material);
  if (identity.publicKeyHex !== record.publicKeyHex || identity.fingerprint !== record.fingerprint) {
    wipe(material.privateKey);
    throw new CorruptVaultError('Vault public key does not match the key it contains');
  }

  return createSession(
    {
      accountId,
      publicKeyHex: identity.publicKeyHex,
      fingerprint: identity.fingerprint,
      partyId: partyIdFor(accountId, identity.publicKeyHex),
    },
    material,
  );
}

/** Minimal UTF-8 decoding (no TextDecoder dependency). */
function decodeUtf8(bytes: Uint8Array): string {
  let out = '';
  let i = 0;
  while (i < bytes.length) {
    const b = bytes[i];
    let code: number;
    if (b < 0x80) {
      code = b;
      i += 1;
    } else if (b >= 0xc0 && b < 0xe0) {
      code = ((b & 0x1f) << 6) | (bytes[i + 1] & 0x3f);
      i += 2;
    } else if (b >= 0xe0 && b < 0xf0) {
      code = ((b & 0x0f) << 12) | ((bytes[i + 1] & 0x3f) << 6) | (bytes[i + 2] & 0x3f);
      i += 3;
    } else {
      code = ((b & 0x07) << 18) | ((bytes[i + 1] & 0x3f) << 12) | ((bytes[i + 2] & 0x3f) << 6) | (bytes[i + 3] & 0x3f);
      i += 4;
    }
    if (code > 0xffff) {
      code -= 0x10000;
      out += String.fromCharCode(0xd800 + (code >>> 10), 0xdc00 + (code & 0x3ff));
    } else {
      out += String.fromCharCode(code);
    }
  }
  return out;
}
